import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LocalizedLink } from '@/components/LocalizedLink';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { useLocalizedPath } from '@/lib/i18n/useLocalizedPath';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { LanguageSwitcher } from './LanguageSwitcher';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Menu, ShoppingCart, X, User, LogOut } from 'lucide-react';
import { useCartStore } from '@/stores/cartStore';
import logo from '@/assets/fake-id-logo-brand.svg';
import { cn } from '@/lib/utils';
import { useMaintenanceActive } from '@/components/MaintenanceBanner';
import { isNavLinkActive } from '@/lib/navActiveHelper';
import { prefetchRoute } from '@/components/LocalizedRoutes';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const navLinks = [
  { key: 'nav.home', href: '/' },
  { key: 'nav.shop', href: '/shop' },
  { key: 'nav.blog', href: '/blog' },
  { key: 'nav.reviews', href: '/reviews' },
  { key: 'nav.about', href: '/about' },
  { key: 'nav.contact', href: '/contact' },
];

export const Header: React.FC = () => {
  const { t } = useLanguage();
  const { getLocalizedPath, stripLanguagePrefix } = useLocalizedPath();
  const location = useLocation();
  const currentPath = stripLanguagePrefix(location.pathname);
  const { user, isAdmin, signOut } = useAuth();
  const cartCount = useCartStore(state => state.cartCount());
  const openCart = useCartStore(state => state.openCart);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const maintenanceActive = useMaintenanceActive();

  // Immediate capitalized email prefix fallback (avoids flash of lowercase)
  const immediateFallback = user?.email
    ? (() => { const p = user.email.split('@')[0]; return p.charAt(0).toUpperCase() + p.slice(1); })()
    : null;

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Fetch customer name for display
  useEffect(() => {
    const fetchDisplayName = async () => {
      if (!user?.email) {
        setDisplayName(null);
        return;
      }
      
      const { data: customer } = await supabase
        .from('customers')
        .select('name')
        .eq('email', user.email)
        .maybeSingle();
      
      if (customer?.name) {
        const firstName = customer.name.split(' ')[0];
        setDisplayName(firstName);
      } else {
        const emailPrefix = user.email.split('@')[0];
        setDisplayName(emailPrefix.charAt(0).toUpperCase() + emailPrefix.slice(1));
      }
    };
    
    fetchDisplayName();
  }, [user?.email]);

  const handleSignOut = async () => {
    await signOut();
  };

  const isLinkActive = (href: string) => isNavLinkActive(currentPath, href);

  return (
    <>
      <header
        className={cn(
          "fixed left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-6xl transition-all duration-300 will-change-transform",
          maintenanceActive ? "top-12" : "top-4",
          scrolled 
            ? "bg-card shadow-lg border border-border/50" 
            : "bg-card/95 border border-border/30",
          "rounded-2xl"
        )}
        style={{ backfaceVisibility: 'hidden', transform: 'translateX(-50%) translateZ(0)' }}
      >
        <div className="px-4 md:px-6 flex h-14 items-center justify-between">
          {/* Logo */}
          <LocalizedLink to="/" className="flex items-center shrink-0">
            <img src={logo} alt="fake-id" className="h-5 w-auto" style={{ minWidth: 100 }} />
          </LocalizedLink>

          {/* Spacer to push nav right */}
          <div className="hidden nav:block flex-1" />

          {/* Desktop Navigation */}
          <nav className="hidden nav:flex items-center gap-1">
            {navLinks.map((link) => (
              <LocalizedLink
                key={link.key}
                to={link.href}
                onMouseEnter={() => prefetchRoute(link.href)}
                className={cn(
                  "px-3 py-2 text-sm font-medium rounded-full transition-colors duration-200 whitespace-nowrap",
                  isLinkActive(link.href)
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                )}
              >
                {t(link.key)}
              </LocalizedLink>
            ))}
          </nav>

          {/* Right side actions */}
          <div className="flex items-center gap-1 ml-3">
            <div className="hidden nav:block">
              <LanguageSwitcher />
            </div>

            {/* Auth Button - Desktop */}
            <div className="hidden nav:block">
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className={cn(
                      "gap-1.5 rounded-full px-3 py-2 text-sm font-medium transition-colors duration-200",
                      isLinkActive('/account')
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    )}>
                      <User className="h-4 w-4" />
                      <span className="max-w-[100px] truncate text-xs font-medium">
                        {displayName || immediateFallback || user.email?.split('@')[0]}
                      </span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-3 py-2.5 mb-1">
                      <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <LocalizedLink to="/account" className="cursor-pointer gap-3">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{t('nav.account')}</span>
                      </LocalizedLink>
                    </DropdownMenuItem>
                    {isAdmin && (
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="cursor-pointer gap-3">
                          <span className="h-4 w-4 text-muted-foreground text-center text-xs">⚡</span>
                          <span className="font-medium">Admin Dashboard</span>
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleSignOut} className="text-primary cursor-pointer gap-3">
                      <LogOut className="h-4 w-4" />
                      <span className="font-medium">{t('nav.logout')}</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button variant="ghost" size="sm" asChild className="text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-full px-3 py-2 text-sm font-medium transition-colors duration-200">
                  <LocalizedLink to="/auth" className="gap-2">
                    <User className="h-4 w-4" />
                    <span className="text-xs">{t('nav.login')}</span>
                  </LocalizedLink>
                </Button>
              )}
            </div>
            
            {/* Cart Button */}
            <button
              className="relative h-9 w-9 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors duration-200"
              onClick={openCart}
              aria-label={t('nav.cart')}
            >
              <ShoppingCart className="h-4.5 w-4.5" />
              {cartCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-primary text-primary-foreground text-[10px] flex items-center justify-center font-bold">
                  {cartCount}
                </span>
              )}
            </button>

            {/* Mobile Menu Trigger */}
            <button
              className="nav:hidden relative h-9 w-9 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors duration-200"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Toggle menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu — Cuberto fullscreen overlay (always mounted for instant response) */}
      <div
        className={cn(
          "fixed inset-0 z-[60] bg-background flex flex-col transition-all duration-300 nav:hidden",
          mobileMenuOpen
            ? "opacity-100 visible"
            : "opacity-0 invisible pointer-events-none"
        )}
        aria-hidden={!mobileMenuOpen}
      >
        {/* Top bar — pixel-perfect match with header: identical positioning method */}
        <div className="fixed top-4 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] max-w-6xl z-20">
          <div className="px-4 md:px-6 flex h-14 items-center justify-between">
            <LocalizedLink to="/" onClick={() => setMobileMenuOpen(false)} className="flex items-center shrink-0">
              <img src={logo} alt="fake-id" className="h-5 w-auto" style={{ minWidth: 100 }} />
            </LocalizedLink>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(false)}
              className="h-9 w-9 rounded-full border border-border/40 bg-card/80 text-foreground shadow-sm transition-colors hover:bg-muted/50 hover:border-border/60"
              aria-label="Close menu"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Navigation links — massive typography */}
        <nav className="flex-1 flex flex-col justify-center px-8 pt-28 -mt-10">
          {navLinks.map((link, i) => (
            <LocalizedLink
              key={link.key}
              to={link.href}
              onClick={() => setMobileMenuOpen(false)}
              className={cn(
                "block py-3 font-display font-black text-4xl uppercase tracking-tight transition-colors duration-200",
                isLinkActive(link.href)
                  ? "text-primary"
                  : "text-foreground/40 hover:text-foreground"
              )}
            >
              {t(link.key)}
            </LocalizedLink>
          ))}

          {/* Admin Link */}
          {isAdmin && (
            <Link
              to="/admin"
              onClick={() => setMobileMenuOpen(false)}
              className="block py-3 font-display font-black text-4xl uppercase tracking-tight text-primary/60 hover:text-primary transition-colors duration-200 mt-2"
            >
              Admin
            </Link>
          )}
        </nav>

        {/* Bottom section — immersive Cuberto style */}
        <div className="px-8 pb-8 space-y-4">
          {/* Divider */}
          <div className="h-px bg-border/30" />

          {user ? (
            <>
              {/* Account card */}
              <LocalizedLink
                to="/account"
                onClick={() => setMobileMenuOpen(false)}
                className={cn(
                  "flex items-center gap-4 p-4 rounded-2xl transition-all duration-300",
                  isLinkActive('/account')
                    ? "bg-primary/15 ring-1 ring-primary/30"
                    : "bg-muted/30 hover:bg-muted/60"
                )}
              >
                <div className="w-11 h-11 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                  <User className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="block text-base font-semibold text-foreground truncate">
                    {displayName || immediateFallback || user.email?.split('@')[0]}
                  </span>
                  <span className="block text-xs text-muted-foreground truncate">{user.email}</span>
                </div>
                <span className="text-xs text-muted-foreground">→</span>
              </LocalizedLink>

              {/* Actions row: Language | Sign Out | Cart */}
              <div className="flex items-center gap-2">
                <div className="shrink-0">
                  <LanguageSwitcher modal={false} />
                </div>
                <button
                  onClick={() => { handleSignOut(); setMobileMenuOpen(false); }}
                  className="h-12 px-4 rounded-full border border-border/40 flex items-center gap-2 text-muted-foreground hover:text-primary hover:border-primary/40 transition-all duration-300 text-sm font-medium"
                >
                  <LogOut className="h-4 w-4" />
                  <span className="hidden xs:inline">{t('nav.logout')}</span>
                </button>
                <button
                  onClick={() => { setMobileMenuOpen(false); openCart(); }}
                  className="flex-1 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center gap-2 text-sm font-semibold hover:bg-foreground hover:text-background transition-all duration-300"
                >
                  <ShoppingCart className="h-4 w-4" />
                  {t('nav.cart')} {cartCount > 0 && `(${cartCount})`}
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Actions row: Language | Sign In | Cart */}
              <div className="flex items-center gap-2">
                <div className="shrink-0">
                  <LanguageSwitcher modal={false} />
                </div>
                <LocalizedLink
                  to="/auth"
                  onClick={() => setMobileMenuOpen(false)}
                  className="h-12 px-5 rounded-full border border-border/40 flex items-center gap-2 text-foreground hover:bg-muted/50 transition-all duration-300 text-sm font-medium"
                >
                  <User className="h-4 w-4" />
                  {t('nav.login')}
                </LocalizedLink>
                <button
                  onClick={() => { setMobileMenuOpen(false); openCart(); }}
                  className="flex-1 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center gap-2 text-sm font-semibold hover:bg-foreground hover:text-background transition-all duration-300"
                >
                  <ShoppingCart className="h-4 w-4" />
                  {t('nav.cart')} {cartCount > 0 && `(${cartCount})`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};
